import { useState, useEffect, useCallback, useRef } from "react";
import { playSound, startBgMusic, stopBgMusic } from "../utils/gameAudio";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

/**
 * Load central game sounds and expose play helpers.
 * Buzzer is excluded — it uses buzzer_configs directly.
 */
export function useGameSounds(gameKey) {
  const [sounds, setSounds] = useState({});
  const [loaded, setLoaded] = useState(false);
  const soundsRef = useRef({});

  useEffect(() => {
    soundsRef.current = sounds;
  }, [sounds]);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);

    fetch(`${API_BASE}/api/game-sounds/${gameKey}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || !data.success) return;
        const map = {};
        (data.sounds || []).forEach((s) => {
          if (s.enabled && s.audio_url) {
            map[s.action_key] = s;
          }
        });
        setSounds(map);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });

    return () => {
      cancelled = true;
      stopBgMusic();
    };
  }, [gameKey]);

  const play = useCallback((actionKey) => {
    const cfg = soundsRef.current[actionKey];
    if (!cfg?.audio_url) return;
    playSound(cfg.audio_url, {
      volume: cfg.volume ?? 1,
      loop: false,
    });
  }, []);

  /** Start (or switch to) a looping track by action key. Stops previous BG loop. */
  const playLoop = useCallback((actionKey) => {
    const cfg = soundsRef.current[actionKey];
    if (!cfg?.audio_url) {
      // Always clear prior loop so a missing track cannot leave intro/lobby music playing.
      stopBgMusic();
      return;
    }
    startBgMusic(cfg.audio_url, { volume: cfg.volume ?? 0.4 });
  }, []);

  const tryStartBgMusic = useCallback(() => {
    playLoop("bg_music");
  }, [playLoop]);

  return {
    sounds,
    loaded,
    play,
    playLoop,
    tryStartBgMusic,
    stopBgMusic,
  };
}

export default useGameSounds;
